import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import type { ContractDetail, DecideContractTypeResponse } from '@clm/shared-types';

import { dismissAmendmentSignal } from '../api/contract-type-proposals';
import { ApiError } from '../api/errors';
import { classifyApiError, type ApiProblemKind } from '../api/problem';
import { mayAttempt } from '../auth/permissions';
import { useIdentity } from '../auth/useIdentity';
import { ContractTypeTag } from '../components/ContractTypeTag';
import { ErrorNotice } from '../components/ErrorNotice';
import { refusalCodeOf } from './base-msa-shared';
import { ContractTypeDecisionPanel, ContractTypeDecisionReceipt } from './ContractTypeDecisionPanel';
import { decisionActions, subjectOfRecord, type DecisionAction } from './contract-type-decision';
import {
  AUTO_CONFIRMATION_KIND_COPY,
  DECISION_KIND_COPY,
  DECISION_NOTE_COPY,
  PROVISIONAL_REASON_COPY,
  SAFETY_SAMPLE_LINE,
  aiTypeHeldWords,
  amendmentSignalWords,
  contractTypeSourceWords,
  legacyKeyCopy,
} from './type-proposal-copy';

/** The roles the gateway grants `admin:configure` to — a display hint, never the gate (D-110-2). */
const TYPE_DECIDER_ROLES: readonly string[] = ['admin'];

/**
 * CLM-110, CLM-118 — the Contract record's type line (DR-10, FR-35, D-110-2,
 * D-110-3; R-110-13, R-110-23, R-110-25, R-118-9, R-118-12, R-118-14).
 *
 * Says how the stored type was decided, and whether it is settled — one of:
 *
 *  - **Type pending** (CLM-118): no type yet — the file name gave none, or
 *    gave conflicting ones — with the API's cause in words. No type word is
 *    printed: the stored `OTHER` is a placeholder (B19);
 *  - **provisional**, with the API's cause in words (`provisional_reason`), and
 *    the AI's reading when it disagrees, never applied by itself;
 *  - **confirmed with no administrator step** (CLM-118, 1a): the file name and
 *    the AI's reading agreed — the reading's model and schema and the act's
 *    audit id, never a person (B8);
 *  - **confirmed** by an administrator on a date — accepted the AI's reading
 *    (with its model and schema), kept against it, corrected, typed, or
 *    confirmed with no reading — plus a notice when a newer reading in the
 *    family disagrees (the agreement's own, or an amendment's: R-110-22);
 *  - an **amendment**, whose type follows its agreement and is decided there;
 *  - an **old key** whose middle segment is not the type (R-110-25): "issued as
 *    a SOW ID before IDs stopped carrying the type". IDs never change;
 *  - **why the AI's type was held** (R-118-9, B16's "with a note"): the type
 *    rule's guard, computed by the API with the pure function the pipeline runs;
 *  - **possibly an amendment** (R-118-14, B13): an amendment word in the name,
 *    or a reading that names an agreement it amends or replaces, with
 *    **Link as an amendment…** (CLM-120, R-120-13) and **Dismiss** for
 *    administrators;
 *  - the **lineage notes** a decision flags but never changes (§3.5).
 *
 * **The decision is an administrator's** (`mayAttempt(identity, ['admin'])`,
 * usability only — the API's `admin:configure` is the gate), offered only when
 * the API says the contract is `decidable`. The panel is the Type proposals
 * page's, and its `seen` block is the API's own — `contract_type_status.seen`,
 * built from the whole family the decision reads (every amendment at any depth,
 * every amendment's reading, the root's latest reading) — posted back unchanged
 * (`subjectOfRecord`). Nothing is rebuilt here and no second read is made, so a
 * decision opened from the record attests to exactly what the decision locks.
 * A Type pending root offers **Accept {AI type}** and **Type…**, never
 * "Confirm OTHER"; an auto-confirmed type offers **Confirm {type}**.
 *
 * **The receipt outlives the reload.** A decision re-reads the record (what is
 * shown is what the database holds); the receipt is held by the page, so it is
 * still on screen when the new record lands.
 */
export function ContractTypeLine({
  contract,
  receipt = null,
  onDecided,
  onReload,
  onLinkAsAmendment,
}: {
  readonly contract: ContractDetail;
  /** The last decision made from this record, kept by the page across its reload. */
  readonly receipt?: DecideContractTypeResponse | null;
  /** Called with the response; the page re-reads the record. Absent, the line offers no action. */
  readonly onDecided?: (response: DecideContractTypeResponse) => void;
  /** Re-read the record — after `stale_view` or `family_changed`, when nothing was written, and after a dismissal. */
  readonly onReload?: () => void;
  /** CLM-120 (R-120-13) — open the record's link dialog on `amends`; absent, the flag offers no link. */
  readonly onLinkAsAmendment?: () => void;
}) {
  const { identity } = useIdentity();
  const status = contract.contract_type_status;
  const proposal = contract.contract_type_proposal;
  const mayAdminister = mayAttempt(identity, TYPE_DECIDER_ROLES);
  /* `null` exactly when the API says the contract is not decidable: an amendment, or awaiting the AI. */
  const subject = subjectOfRecord(contract);
  const mayDecide = onDecided !== undefined && subject !== null && mayAdminister;
  /*
   * A confirmed type with no newer reading can only be corrected: re-confirming the same type is the
   * API's `400 no_change`, so the line does not offer it. An auto-confirmed type is not settled by a
   * person, so it keeps Confirm {type} (CLM-118, §3.6).
   */
  const settled = status.confirmation === 'confirmed' && !status.newer_reading_disagrees;
  const actions =
    subject === null
      ? []
      : decisionActions(subject).filter((action) => !settled || action.type !== contract.contract_type);
  const [open, setOpen] = useState<DecisionAction | null>(null);

  /* A different contract, or the same one re-read after a decision: whatever was open was about the old rows. */
  useEffect(
    () => () => {
      setOpen(null);
    },
    [contract],
  );

  const evidence =
    proposal === null ? null : (
      <>
        (<code>{proposal.extraction_model}</code>, schema <code>{proposal.extraction_schema_version}</code>)
      </>
    );
  /*
   * The stored proposal is the agreement's own latest reading exactly when `seen` names it; only then
   * may the newer-reading notice quote it. Otherwise the newer reading is an amendment's, or has no
   * usable type, and the notice says so without naming a type it cannot attribute.
   */
  const ownLatestDisagrees =
    proposal !== null &&
    status.seen !== null &&
    status.seen.proposal_audit_id === proposal.audit_id &&
    proposal.proposed_type !== contract.contract_type;
  const auto = status.auto_confirmation;
  /* The reading the act cites, when it is the stored proposal — its model and schema are then the act's evidence. */
  const autoReading = auto !== null && proposal !== null && proposal.audit_id === auto.reading_audit_id ? proposal : null;
  /* The reading the held type came from: the stored proposal, or the root's latest as `seen` names it. */
  const heldReading = proposal?.proposed_type ?? status.seen?.proposed_type ?? null;
  const settledWords = status.confirmation === 'auto_confirmed' ? 'settled' : 'confirmed';

  return (
    <div data-testid="contract-type-line">
      <p className="mut" data-testid="contract-type-source">
        {/* "Type pending" names itself: no "Type" lead-in before it. */}
        {status.confirmation === 'pending' ? null : 'Type '}
        <ContractTypeTag type={contract.contract_type} confirmation={status.confirmation} autoKind={status.auto_confirmation?.kind ?? null} /> —{' '}
        {contractTypeSourceWords(contract.contract_type_source, status.confirmation)}.
      </p>

      <p className="mut" data-testid="contract-type-status">
        {status.follows_agreement !== null ? (
          <>
            Follows its agreement{' '}
            <Link to={`/contracts/record/${encodeURIComponent(status.follows_agreement)}`}>
              <code>{status.follows_agreement}</code>
            </Link>
            : an amendment’s type is its agreement’s, and is decided there.
            {status.decision === null ? null : (
              <>
                {' '}
                Confirmed by {status.decision.decided_by_upn} on{' '}
                <time dateTime={status.decision.decided_utc}>{status.decision.decided_utc.slice(0, 10)}</time>.
              </>
            )}
            {auto === null ? null : (
              <> Confirmed with no administrator step — {AUTO_CONFIRMATION_KIND_COPY[auto.kind]}.</>
            )}
          </>
        ) : status.decision !== null ? (
          <>
            Confirmed by {status.decision.decided_by_upn} on{' '}
            <time dateTime={status.decision.decided_utc}>{status.decision.decided_utc.slice(0, 10)}</time> —{' '}
            {DECISION_KIND_COPY[status.decision.kind]}
            {status.decision.kind === 'accepted_ai' && evidence !== null ? <> {evidence}</> : null}.
          </>
        ) : auto !== null ? (
          <span data-testid="contract-type-auto-confirmation">
            Confirmed with no administrator step — {AUTO_CONFIRMATION_KIND_COPY[auto.kind]} (
            {autoReading === null ? null : (
              <>
                <code>{autoReading.extraction_model}</code>, schema <code>{autoReading.extraction_schema_version}</code>,{' '}
              </>
            )}
            audit {auto.audit_id}), on <time dateTime={auto.settled_utc}>{auto.settled_utc.slice(0, 10)}</time>. No
            person has checked it.
          </span>
        ) : status.provisional_reason !== null ? (
          <>
            {status.confirmation === 'pending' ? 'Type pending' : 'Provisional'} —{' '}
            {PROVISIONAL_REASON_COPY[status.provisional_reason]}.
          </>
        ) : null}
      </p>

      {/* CLM-121 (R-121-15) — a safety sample is visible, never blind: correcting it pauses auto-approval. */}
      {status.auto_approval_sample ? (
        <p className="mut" data-testid="contract-type-safety-sample">
          {SAFETY_SAMPLE_LINE}
        </p>
      ) : null}

      {status.ai_type_held === null ? null : (
        <p className="mut" data-testid="contract-type-held">
          The AI’s {heldReading ?? 'type'} was not applied:{' '}
          {aiTypeHeldWords(
            status.ai_type_held,
            contract.lineage.parent_contract_key ?? contract.lineage.supersedes_contract_key ?? null,
          )}
          . An administrator decides.
        </p>
      )}

      {/* A pending row has its own line above: there is no type for the reading to disagree with. */}
      {status.confirmation === 'provisional' && proposal !== null && proposal.agrees === false ? (
        <p className="banner up" role="status" data-testid="contract-type-proposal">
          The AI read this document as <strong>{proposal.proposed_type}</strong> {evidence}. Not applied: the type stays{' '}
          {contract.contract_type} until an administrator decides.
        </p>
      ) : null}

      {status.newer_reading_disagrees ? (
        <p className="banner up" role="status" data-testid="contract-type-newer-reading">
          {ownLatestDisagrees && proposal !== null ? (
            <>
              A newer AI reading disagrees with the {settledWords} type: it reads the document as{' '}
              <strong>{proposal.proposed_type}</strong> {evidence}.
            </>
          ) : status.follows_agreement === null ? (
            <>
              A newer AI reading in this agreement’s family — of an amendment, or with no usable type — disagrees with
              the {settledWords} type.
            </>
          ) : (
            <>A newer AI reading of this amendment disagrees with its agreement’s {settledWords} type.</>
          )}{' '}
          The type stays {contract.contract_type} until an administrator decides
          {status.decision === null ? '' : ' again'}
          {status.follows_agreement === null ? '' : ' on the agreement'}.
        </p>
      ) : null}

      {status.legacy_key_type_segment === null ? null : (
        <p className="mut" data-testid="contract-type-legacy-key">
          {legacyKeyCopy(status.legacy_key_type_segment, contract.contract_type)}
        </p>
      )}

      <AmendmentSignalNote
        contract={contract}
        mayDismiss={mayAdminister && onReload !== undefined}
        onDismissed={onReload}
        onLink={mayAdminister ? onLinkAsAmendment : undefined}
      />

      {status.lineage_notes.length === 0 ? null : (
        <ul className="mut" aria-label="Type lineage notes" data-testid="contract-type-lineage">
          {status.lineage_notes.map((lineage) => (
            <li key={lineage.kind}>
              {DECISION_NOTE_COPY[lineage.kind]}{' '}
              {lineage.contract_keys.map((noted) => (
                <Link key={noted} to={`/contracts/record/${encodeURIComponent(noted)}`}>
                  <code>{noted}</code>{' '}
                </Link>
              ))}
            </li>
          ))}
        </ul>
      )}

      {receipt === null ? null : <ContractTypeDecisionReceipt response={receipt} />}

      {mayDecide && open === null ? (
        <span className="fl" role="group" aria-label={`Decide the type of ${contract.contract_key}`}>
          {actions.map((action) => (
            <button key={action.id} type="button" className="btn sm qt" onClick={() => setOpen(action)}>
              {action.label}
            </button>
          ))}
        </span>
      ) : null}
      {open !== null && subject !== null && onDecided !== undefined ? (
        <ContractTypeDecisionPanel
          subject={subject}
          action={open}
          onDecided={(response) => {
            setOpen(null);
            onDecided(response);
          }}
          onCancel={() => setOpen(null)}
          onReload={() => {
            setOpen(null);
            onReload?.();
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * CLM-118 (R-118-14, B13) — "possibly an amendment filed on its own". The flag
 * never changes the type. An administrator may link the contract to its
 * agreement (CLM-120, R-120-13: **Link as an amendment…** opens the record's link
 * dialog on `amends`, and the flag clears by its own predicate once linked) or
 * dismiss it (`dismiss-amendment-signal`); the page then re-reads the record, so
 * what is shown is what the database holds. A dismissed flag says who dismissed
 * it and when.
 */
function AmendmentSignalNote({
  contract,
  mayDismiss,
  onDismissed,
  onLink,
}: {
  readonly contract: ContractDetail;
  readonly mayDismiss: boolean;
  readonly onDismissed: (() => void) | undefined;
  readonly onLink: (() => void) | undefined;
}) {
  const signal = contract.amendment_signal;
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<{
    readonly kind: ApiProblemKind;
    readonly code: string | null;
    readonly correlation_id: string | null;
  } | null>(null);

  if (signal === null) return null;
  if (!signal.flagged) {
    return signal.dismissed_utc === null || signal.dismissed_by_upn === null ? null : (
      <p className="mut" data-testid="contract-amendment-signal-dismissed">
        A “possibly an amendment” flag was dismissed by {signal.dismissed_by_upn} on{' '}
        <time dateTime={signal.dismissed_utc}>{signal.dismissed_utc.slice(0, 10)}</time>.
      </p>
    );
  }

  async function dismiss() {
    setSaving(true);
    setProblem(null);
    try {
      await dismissAmendmentSignal(contract.contract_key);
      onDismissed?.();
    } catch (error) {
      setProblem({
        kind: classifyApiError(error),
        code: refusalCodeOf(error),
        correlation_id: error instanceof ApiError ? error.correlation_id : null,
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="banner up" role="status" data-testid="contract-amendment-signal">
      <p>
        Possibly an amendment filed on its own: {amendmentSignalWords(signal)}. If it amends a filed agreement, an
        administrator links it here; its type is not changed by this flag.
      </p>
      {onLink === undefined ? null : (
        <button type="button" className="btn sm qt" disabled={saving} onClick={onLink}>
          Link as an amendment…
        </button>
      )}
      {mayDismiss ? (
        <button type="button" className="btn sm qt" disabled={saving} onClick={() => void dismiss()}>
          {saving ? 'Dismissing…' : 'Dismiss'}
        </button>
      ) : null}
      {problem === null ? null : problem.kind === 'conflict' && problem.code === 'no_amendment_signal' ? (
        <p className="mut" data-testid="contract-amendment-signal-gone">
          This contract is no longer flagged — it was dismissed or linked since the page loaded. Nothing was written.
        </p>
      ) : (
        <ErrorNotice kind={problem.kind} correlation_id={problem.correlation_id} />
      )}
    </div>
  );
}
